/**
 * TBMuktBharat Competition List Page
 * Student competitions for TB awareness (Poster, Quiz, Essay, Drawing, Slogan)
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { GovLayout, GovPageTitle, GovCard, GovButton, GovEmptyState } from '@/components/layout/GovLayout'
import { Calendar, Users, Trophy, Search, Clock, Award, Filter, Loader2 } from 'lucide-react'
import { motion } from 'framer-motion'
import competitionService, { Competition, CompetitionType, CompetitionStatus } from '@/services/competitionService'
import { useAuthStore } from '@/stores/authStore'

// ================================================================================
// COMPETITION TYPE CONFIG
// ================================================================================
const COMPETITION_TYPES: { value: CompetitionType; label: string; icon: string }[] = [
  { value: 'POSTER', label: 'Poster', icon: '🎨' },
  { value: 'QUIZ', label: 'Quiz', icon: '🧠' },
  { value: 'ESSAY', label: 'Essay', icon: '📝' },
  { value: 'DRAWING', label: 'Drawing', icon: '🖌️' },
  { value: 'SLOGAN', label: 'Slogan', icon: '💬' },
]

const STATUS_CONFIG: Record<CompetitionStatus, { label: string; color: string }> = {
  DRAFT: { label: 'Draft', color: 'bg-gray-100 text-gray-700' },
  ACTIVE: { label: 'Active', color: 'bg-green-100 text-green-700' },
  CLOSED: { label: 'Closed', color: 'bg-red-100 text-red-700' },
  ARCHIVED: { label: 'Archived', color: 'bg-gray-100 text-gray-500' },
}

// ================================================================================
// MOCK DATA
// ================================================================================
const MOCK_COMPETITIONS: Competition[] = [
  {
    id: 1,
    title: 'TB Awareness Poster Competition 2026',
    description: 'Create a creative poster spreading awareness about TB prevention and treatment. Open to students of all ages.',
    type: 'POSTER',
    typeDisplayName: 'Poster Competition',
    status: 'ACTIVE',
    statusDisplayName: 'Active',
    startDate: '2026-06-01',
    endDate: '2026-07-31',
    resultDate: '2026-08-15',
    maxParticipants: 500,
    currentParticipants: 156,
    prizeDescription: 'First Prize: ₹10,000 | Second Prize: ₹5,000 | Third Prize: ₹2,500',
    isFeatured: true,
    isActive: true,
    isExpired: false,
    daysRemaining: 46,
  },
  {
    id: 2,
    title: 'TB Quiz Championship',
    description: 'Test your knowledge about tuberculosis in this exciting quiz competition. Multiple rounds with elimination.',
    type: 'QUIZ',
    typeDisplayName: 'Quiz Competition',
    status: 'ACTIVE',
    statusDisplayName: 'Active',
    startDate: '2026-06-15',
    endDate: '2026-07-15',
    resultDate: '2026-07-30',
    maxParticipants: 1000,
    currentParticipants: 423,
    prizeDescription: 'First Prize: ₹15,000 | Second Prize: ₹8,000 | Third Prize: ₹4,000',
    isFeatured: true,
    isActive: true,
    isExpired: false,
    daysRemaining: 30,
  },
  {
    id: 3,
    title: 'Essay Writing: My Vision of TB-Free India',
    description: 'Write an essay expressing your vision for a TB-free India. Maximum 1500 words.',
    type: 'ESSAY',
    typeDisplayName: 'Essay Competition',
    status: 'ACTIVE',
    statusDisplayName: 'Active',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    resultDate: '2026-09-15',
    maxParticipants: 300,
    currentParticipants: 89,
    prizeDescription: 'First Prize: ₹12,000 | Second Prize: ₹6,000 | Third Prize: ₹3,000',
    isFeatured: false,
    isActive: true,
    isExpired: false,
    daysRemaining: 77,
  },
  {
    id: 4,
    title: 'Drawing Contest: Fight Against TB',
    description: 'Express your creativity through art! Draw your interpretation of the fight against tuberculosis.',
    type: 'DRAWING',
    typeDisplayName: 'Drawing Competition',
    status: 'ACTIVE',
    statusDisplayName: 'Active',
    startDate: '2026-06-10',
    endDate: '2026-08-10',
    resultDate: '2026-08-25',
    maxParticipants: 400,
    currentParticipants: 201,
    prizeDescription: 'First Prize: ₹8,000 | Second Prize: ₹4,000 | Third Prize: ₹2,000',
    isFeatured: false,
    isActive: true,
    isExpired: false,
    daysRemaining: 55,
  },
  {
    id: 5,
    title: 'Slogan Competition: End TB Together',
    description: 'Create a powerful slogan to spread TB awareness. Maximum 15 words.',
    type: 'SLOGAN',
    typeDisplayName: 'Slogan Competition',
    status: 'ACTIVE',
    statusDisplayName: 'Active',
    startDate: '2026-06-20',
    endDate: '2026-07-20',
    resultDate: '2026-08-05',
    maxParticipants: 2000,
    currentParticipants: 567,
    prizeDescription: 'First Prize: ₹5,000 | Second Prize: ₹3,000 | Third Prize: ₹1,500',
    isFeatured: false,
    isActive: true,
    isExpired: false,
    daysRemaining: 35,
  },
  {
    id: 6,
    title: 'TB Awareness Poster 2025 - Results Declared',
    description: 'Previous year poster competition results are now available.',
    type: 'POSTER',
    typeDisplayName: 'Poster Competition',
    status: 'CLOSED',
    statusDisplayName: 'Closed',
    startDate: '2025-06-01',
    endDate: '2025-07-31',
    resultDate: '2025-08-15',
    maxParticipants: 500,
    currentParticipants: 489,
    isFeatured: false,
    isActive: false,
    isExpired: true,
    daysRemaining: 0,
  },
]

// ================================================================================
// COMPONENTS
// ================================================================================

const CompetitionCard = React.memo(({ competition }: { competition: Competition }) => {
  const typeConfig = COMPETITION_TYPES.find(t => t.value === competition.type) || COMPETITION_TYPES[0]
  const statusConfig = STATUS_CONFIG[competition.status] || STATUS_CONFIG.ACTIVE

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Link to={`/competitions/${competition.id}`}>
        <GovCard className="hover:shadow-lg transition-all cursor-pointer group">
          {/* Banner */}
          {competition.bannerImageUrl ? (
            <div className="h-32 -mx-4 -mt-4 mb-4 overflow-hidden rounded-t-xl bg-gray-100">
              <img
                src={competition.bannerImageUrl}
                alt={competition.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          ) : (
            <div className="h-32 -mx-4 -mt-4 mb-4 rounded-t-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center">
              <span className="text-4xl">{typeConfig.icon}</span>
            </div>
          )}

          {/* Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${statusConfig.color}`}>
                  {statusConfig.label}
                </span>
                {competition.isFeatured && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-amber-100 text-amber-700">
                    ⭐ Featured
                  </span>
                )}
              </div>
              <h3 className="font-bold text-gray-900 group-hover:text-blue-600 transition-colors line-clamp-2">
                {competition.title}
              </h3>
            </div>
          </div>

          {/* Type Badge */}
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm px-3 py-1 bg-blue-50 text-blue-700 rounded-full">
              {typeConfig.icon} {typeConfig.label}
            </span>
          </div>

          {/* Description */}
          <p className="text-sm text-gray-600 mb-4 line-clamp-2">
            {competition.description}
          </p>

          {/* Stats */}
          <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
            {competition.daysRemaining !== undefined && competition.daysRemaining > 0 && (
              <div className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                <span>{competition.daysRemaining} days left</span>
              </div>
            )}
            {competition.currentParticipants !== undefined && (
              <div className="flex items-center gap-1">
                <Users className="w-4 h-4" />
                <span>{competition.currentParticipants} participants</span>
              </div>
            )}
          </div>

          {/* Prize */}
          {competition.prizeDescription && (
            <div className="flex items-center gap-2 text-sm bg-amber-50 text-amber-700 px-3 py-2 rounded-lg">
              <Award className="w-4 h-4" />
              <span className="line-clamp-1">{competition.prizeDescription.split('|')[0]}</span>
            </div>
          )}

          {/* Submit Button */}
          {competition.status === 'ACTIVE' && !competition.isExpired && (
            <div className="mt-4 pt-3 border-t border-gray-100">
              <GovButton variant="primary" className="w-full">
                {competition.hasSubmitted ? 'View Submission' : 'Submit Entry'}
              </GovButton>
            </div>
          )}
        </GovCard>
      </Link>
    </motion.div>
  )
})

CompetitionCard.displayName = 'CompetitionCard'

// ================================================================================
// MAIN COMPONENT
// ================================================================================

const CompetitionListPage: React.FC = () => {
  const { isAuthenticated } = useAuthStore()
  const [competitions, setCompetitions] = useState<Competition[]>([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedType, setSelectedType] = useState<CompetitionType | 'ALL'>('ALL')
  const [selectedStatus, setSelectedStatus] = useState<CompetitionStatus | 'ALL'>('ACTIVE')

  useEffect(() => {
    fetchCompetitions()
  }, [selectedType, selectedStatus])

  const fetchCompetitions = async () => {
    setLoading(true)
    try {
      const response = await competitionService.getCompetitions({
        type: selectedType !== 'ALL' ? selectedType : undefined,
        status: selectedStatus !== 'ALL' ? selectedStatus : undefined,
        search: searchQuery || undefined,
        page: 0,
        size: 20,
      })
      setCompetitions(response.content.length > 0 ? response.content : MOCK_COMPETITIONS)
    } catch (error) {
      console.error('Failed to fetch competitions:', error)
      setCompetitions(MOCK_COMPETITIONS)
    } finally {
      setLoading(false)
    }
  }

  const filteredCompetitions = useMemo(() => {
    let filtered = competitions

    if (selectedType !== 'ALL') {
      filtered = filtered.filter(c => c.type === selectedType)
    }

    if (selectedStatus !== 'ALL') {
      filtered = filtered.filter(c => c.status === selectedStatus)
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(c =>
        c.title.toLowerCase().includes(query) ||
        c.description?.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [competitions, selectedType, selectedStatus, searchQuery])

  const handleSearch = useCallback((e: React.FormEvent) => {
    e.preventDefault()
    fetchCompetitions()
  }, [searchQuery])

  return (
    <GovLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <GovPageTitle
          title="Competitions"
          subtitle="Participate in TB awareness competitions and win exciting prizes"
          icon={Trophy}
        />

        {/* Create Competition CTA - any logged-in user may create (saved as draft) */}
        {isAuthenticated && (
          <div className="flex justify-end mb-4">
            <Link to="/competitions/create">
              <GovButton size="lg">+ Create Competition</GovButton>
            </Link>
          </div>
        )}

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Active Competitions', value: competitions.filter(c => c.status === 'ACTIVE').length, color: 'bg-green-500' },
            { label: 'Total Participants', value: competitions.reduce((sum, c) => sum + (c.currentParticipants || 0), 0), color: 'bg-blue-500' },
            { label: 'Upcoming Deadlines', value: competitions.filter(c => c.daysRemaining && c.daysRemaining < 30).length, color: 'bg-amber-500' },
            { label: 'Prize Pool', value: '₹2L+', color: 'bg-purple-500' },
          ].map((stat, i) => (
            <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
              <div className={`text-2xl font-bold ${stat.color.replace('bg-', 'text-')}`}>
                {stat.value}
              </div>
              <div className="text-sm text-gray-500">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 mb-6">
          <div className="flex flex-col md:flex-row gap-4">
            {/* Search */}
            <form onSubmit={handleSearch} className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search competitions..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </form>

            {/* Type Filter */}
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={selectedType}
                onChange={e => setSelectedType(e.target.value as CompetitionType | 'ALL')}
                className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="ALL">All Types</option>
                {COMPETITION_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.icon} {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter */}
            <select
              value={selectedStatus}
              onChange={e => setSelectedStatus(e.target.value as CompetitionStatus | 'ALL')}
              className="border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="CLOSED">Closed</option>
              <option value="DRAFT">Draft</option>
            </select>
          </div>
        </div>

        {/* Competition Grid */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
          </div>
        ) : filteredCompetitions.length === 0 ? (
          <GovEmptyState
            icon="🏆"
            title="No competitions found"
            description="Try adjusting your filters or check back later for new competitions"
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredCompetitions.map(competition => (
              <CompetitionCard key={competition.id} competition={competition} />
            ))}
          </div>
        )}
      </div>
    </GovLayout>
  )
}

export default CompetitionListPage